import { useEffect, useRef } from 'react';
import logger from '../services/logger';
import api from '../services/api';
import { API_BASE_URL } from '../config';

export function useListeningTracker({ progressMs, durationMs, isPlaying, currentTrackIndex, playlist, platform }) {

  const accumulatedMsRef   = useRef(0);
  const lastProgressMsRef  = useRef(null); // null = first tick not yet seen
  const lastWallClockRef   = useRef(performance.now());
  const trackedIndexRef    = useRef(currentTrackIndex);
  const trackedUriRef      = useRef(null);
  const trackedNameRef     = useRef(null);
  const trackedArtistsRef  = useRef(null);
  const trackedDurationRef = useRef(0);
  const platformRef        = useRef(platform);
  const supabaseTokenRef   = useRef(null); // cached for synchronous use in unmount flush

  // Keep platformRef current and refresh token cache
  useEffect(() => { platformRef.current = platform; }, [platform]);
  useEffect(() => {
    api.getSupabaseToken().then(t => { supabaseTokenRef.current = t; });
  }); // runs every render — keeps token fresh without blocking

  // ACCUMULATOR — fires every 250ms tick from setProgress
  useEffect(() => {
    if (!isPlaying) return;
    if (trackedIndexRef.current !== currentTrackIndex) return;

    // Skip first tick to avoid accumulating a spurious initial delta from 0
    if (lastProgressMsRef.current === null) {
      lastProgressMsRef.current = progressMs;
      lastWallClockRef.current  = performance.now();
      return;
    }

    const delta  = progressMs - lastProgressMsRef.current;
    const wallDt = performance.now() - lastWallClockRef.current;
    const isClean = delta > 0 && delta < 2000 && Math.abs(delta - wallDt) < wallDt * 0.6;

    if (isClean) accumulatedMsRef.current += delta;

    lastProgressMsRef.current = progressMs;
    lastWallClockRef.current  = performance.now();

    if (durationMs > 0 && trackedDurationRef.current === 0) {
      trackedDurationRef.current = durationMs;
    }
  }, [progressMs]); // eslint-disable-line react-hooks/exhaustive-deps

  // FLUSH ON TRACK CHANGE (component still live — can use async)
  useEffect(() => {
    flushRecord(false);

    const track = playlist?.tracks?.[currentTrackIndex] ?? null;
    accumulatedMsRef.current   = 0;
    lastProgressMsRef.current  = null;
    lastWallClockRef.current   = performance.now();
    trackedIndexRef.current    = currentTrackIndex;
    trackedUriRef.current      = track?.uri ?? null;
    trackedNameRef.current     = track?.name ?? null;
    trackedArtistsRef.current  = track?.artists ?? null;
    trackedDurationRef.current = track?.duration_ms ?? 0;

    logger.debug(`[ListenTracker] tracking idx=${currentTrackIndex} uri=${trackedUriRef.current}`);
  }, [currentTrackIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // FLUSH ON UNMOUNT (keepalive=true survives page close without async blocking)
  useEffect(() => () => flushRecord(true), []); // eslint-disable-line react-hooks/exhaustive-deps

  async function flushRecord(isUnmount = false) {
    const uri     = trackedUriRef.current;
    const accumMs = accumulatedMsRef.current;
    const durMs   = trackedDurationRef.current;

    if (!uri || accumMs < 10000) {
      if (uri) logger.debug(`[ListenTracker] skip flush uri=${uri} accumMs=${accumMs}`);
      return;
    }

    const completionPct = durMs > 0 ? Math.min(100, Math.round((accumMs / durMs) * 100)) : 0;
    logger.info(`[ListenTracker] flush uri=${uri} accumMs=${accumMs} completion=${completionPct}% unmount=${isUnmount}`);

    const body = JSON.stringify({
      track_uri:            uri,
      track_name:           trackedNameRef.current,
      artists:              trackedArtistsRef.current,
      platform:             platformRef.current || 'spotify',
      duration_listened_ms: accumMs,
      duration_ms:          durMs,
      completion_pct:       completionPct,
    });

    const token = isUnmount
      ? supabaseTokenRef.current                // synchronous — cached ref
      : await api.getSupabaseToken();           // async — component still live

    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['X-Supabase-Token'] = token;

    try {
      await fetch(`${API_BASE_URL}/api/listening-history/track`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,   // survives page unload on both paths
        headers,
        body,
      });
    } catch (err) {
      logger.warn('[ListenTracker] flush failed', err);
    }
  }
}
